import React from 'react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { type UseDraftBemanningReturn } from '@/hooks/useDraftBemanning';
import { useDraftBemanningDrawer } from './draft-bemanning-drawer/hooks/useDraftBemanningDrawer';
import { DrawerHeader } from './draft-bemanning-drawer/components/DrawerHeader';
import { DrawerContent } from './draft-bemanning-drawer/components/DrawerContent';

interface DraftBemanningDrawerProps {
  open: boolean;
  onClose: () => void;
  produksjonId: number;
  selectedTalent: {
    talent_navn: string;
    talent_kategori: string;
    antall: number;
    antallFylt: number;
  } | null;
  draft: UseDraftBemanningReturn;
  onSuccess: () => void;
  onPinToggle: (pinned: boolean) => void;
  isPinned: boolean;
}

const DraftBemanningDrawer: React.FC<DraftBemanningDrawerProps> = ({
  open,
  onClose,
  produksjonId,
  selectedTalent,
  draft,
  onSuccess,
  onPinToggle,
  isPinned,
}) => {
  const {
    loading,
    error,
    selectedUsers,
    showAllUsers,
    searchQuery,
    displayList,
    setShowAllUsers,
    setSearchQuery,
    toggleUserSelection,
    handleAddToDraft,
  } = useDraftBemanningDrawer({
    open,
    produksjonId,
    selectedTalent,
    draft,
  });

  if (!open || !selectedTalent) return null;

  const headerProps = {
    title: selectedTalent.talent_navn,
    description: selectedTalent.talent_kategori,
    isPinned,
    onClose,
    onPinToggle: () => onPinToggle(!isPinned),
  };

  const contentProps = {
    searchQuery,
    showAllUsers,
    displayList,
    loading,
    error,
    selectedUsers,
    selectedTalentName: selectedTalent.talent_navn,
    draft,
    onSearchChange: setSearchQuery,
    onShowAllUsersChange: setShowAllUsers,
    onToggleUser: toggleUserSelection,
    onAddToDraft: handleAddToDraft,
  };

  // Hvis pinned, render som fixed sidebar
  if (isPinned) {
    return (
      <div className="fixed top-0 right-0 h-screen w-full sm:w-[500px] bg-card border-l border-border flex flex-col shadow-2xl z-40">
        <DrawerHeader {...headerProps} variant="pinned" />
        <DrawerContent {...contentProps} />
      </div>
    );
  }

  // Hvis ikke pinned, render som Sheet overlay
  return (
    <Sheet open={open} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        overlayClassName="bg-black/10"
        className="w-full sm:w-[500px] p-0 flex flex-col shadow-2xl"
      >
        <DrawerHeader {...headerProps} variant="sheet" />
        <DrawerContent {...contentProps} />
      </SheetContent>
    </Sheet>
  );
};

export default DraftBemanningDrawer;
